import { stairOpeningBounds } from "../../../../stair_opening"
import { Container, Graphics } from "pixi.js"
import { Entity, StairTravelView, TileSize } from "game-common/models"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { stairTravelPose } from "game-common/stair_travel"

interface StairSprite { x: number; y: number; scale?: { x: number; y: number }; mask?: Container["mask"]; addChild?: Container["addChild"] }
interface Playback { id: string; endsAt: number; clip?: Graphics; previousMask?: Container["mask"] }
const playback = new WeakMap<StairSprite, Playback>()

const stateFor = (view: StairTravelView, sprite: StairSprite, now: number): Playback => {
    let state = playback.get(sprite)
    if (!state || state.id !== view.id) {
        release(sprite)
        state = { id: view.id, endsAt: now + view.remainingMs }
        playback.set(sprite, state)
    } else state.endsAt = Math.min(state.endsAt, now + view.remainingMs)
    return state
}
const elapsedFor = (view: StairTravelView, sprite: StairSprite, now: number): number =>
    view.climb?.elapsedMs ?? view.durationMs - (stateFor(view, sprite, now).endsAt - now)
const poseFor = (view: StairTravelView, sprite: StairSprite, now: number) =>
    stairTravelPose(view, elapsedFor(view, sprite, now))

/** A body on a ladder, as the renderer sees it: how far up it has climbed (stairTravelPose ladderRise). */
export interface LadderClimb {
    ladderRise?: number
}

export const ladderRiseOf = (entity: Entity): number | undefined => (entity as Entity & LadderClimb).ladderRise

export const isStairTravelVisible = (entity: Entity, sprite: StairSprite, now = Date.now(), roomId = entity.roomId): boolean =>
    !!entity.stairTravel && entity.stairTravel.roomId === roomId && !entity.floorFall &&
    (!entity.stairTravel.projection || (entity.stairTravel.phase === "depart"
        ? elapsedFor(entity.stairTravel, sprite, now) >= entity.stairTravel.walkMs
        : elapsedFor(entity.stairTravel, sprite, now) < entity.stairTravel.durationMs)) &&
    (!!entity.stairTravel.climb || entity.stairTravel.phase === "depart" || stateFor(entity.stairTravel, sprite, now).endsAt > now)

export const stairTravelForRendering = (entity: Entity, sprite: StairSprite, now = Date.now(), roomId = entity.roomId): Entity => {
    if (!isStairTravelVisible(entity, sprite, now, roomId)) return entity
    const view = entity.stairTravel
    const pose = poseFor(view, sprite, now)
    const climb: LadderClimb = { ladderRise: "ladderRise" in pose ? pose.ladderRise : undefined }
    return Object.assign(Object.create(entity), {
        location: { ...entity.location, ...pose.location },
        movement: { ...entity.movement, moving: pose.walking, direction: pose.direction, angle: pose.angle },
        ...climb,
    })
}

const releaseClip = (sprite: StairSprite, state: Playback) => {
    if (!state.clip) return
    if (sprite.mask === state.clip) sprite.mask = state.previousMask
    state.clip.parent?.removeChild(state.clip)
    state.clip.destroy()
    state.clip = undefined
}
const release = (sprite: StairSprite) => {
    const state = playback.get(sprite)
    if (state) {
        releaseClip(sprite, state)
    }
    playback.delete(sprite)
}

/** Keep upright climbs visible; clip the opening of downward stairs. */
/** `hillLift`: how far the body is drawn up a hill (hill_lift.ts), which the stair's own edges, in its storey's px, are too. */
export const applyStairTravel = (entity: Entity, sprite: StairSprite, now = Date.now(), roomId = entity.roomId, hillLift = 0): void => {
    if (!isStairTravelVisible(entity, sprite, now, roomId)) {
        release(sprite)
        if (entity.stairTravel?.projection) {
            const state = stateFor(entity.stairTravel, sprite, now)
            state.previousMask = sprite.mask
            state.clip = new Graphics()
            sprite.addChild?.(state.clip)
            sprite.mask = state.clip
        }
        return
    }
    const state = stateFor(entity.stairTravel, sprite, now)
    const pose = poseFor(entity.stairTravel, sprite, now)
    const scale = Math.abs(sprite.scale?.y || 1)
    sprite.x += pose.location.x - entity.location.x
    sprite.y += pose.location.y - entity.location.y + pose.bodyOffset * scale
    const split = "split" in pose ? pose.split : undefined
    if (entity.stairTravel.flight && !split || !entity.stairTravel.flight && "ladderRise" in pose && !pose.below) {
        releaseClip(sprite, state)
        return
    }
    if (!pose.transitioning && !split) { releaseClip(sprite, state); return }
    if (!sprite.addChild) return
    if (!state.clip) {
        state.previousMask = sprite.mask
        state.clip = new Graphics()
        sprite.addChild(state.clip)
        sprite.mask = state.clip
    }
    // A child mask is cleaned up automatically when its sprite is destroyed.
    // Compensate for body movement so the clipping plane stays fixed at the stair.
    const view = entity.stairTravel
    const opening = view.ramp && pose.below
        ? view.phase === "depart" ? view.end : view.start : undefined
    const plane = split ? (split.edge - hillLift - sprite.y) / scale : opening
        // Derive local mask coordinates from the actual rendered body transform so
        // body lift, scale and interpolation cannot move the floor's clipping edge.
        ? (stairOpeningBounds({ x: Math.floor(opening.x / TileSize), y: Math.floor(opening.y / TileSize) }).south - hillLift - sprite.y) / scale
        : pose.headClip !== undefined
            ? pose.below ? BODY_GROUND_OFFSET - 64 * pose.headClip : BODY_GROUND_OFFSET - 64 + 64 * pose.headClip
            : (pose.below ? BODY_GROUND_OFFSET : BODY_GROUND_OFFSET - 64) - pose.bodyOffset - pose.entranceOffset
    const above = split ? split.above : pose.below
    state.clip.clear().beginFill(0xffffff)
        .drawRect(-256, above ? plane - 512 : plane, 512, 512).endFill()
}
